/**
 * 網絡狀態監聽 Hook
 * 監聽瀏覽器的 online/offline 事件，提供即時的網絡連線狀態
 * 用於離線功能判斷，例如：是否啟用離線模式、是否觸發同步等
 */
import { useState, useEffect, useCallback } from 'react';

/** 網絡狀態返回值結構 */
interface NetworkStatus {
  /** 當前是否有網絡連線 */
  isOnline: boolean;
}

/**
 * useNetworkStatus — 網絡連線狀態監聽 Hook
 *
 * 功能說明：
 * - 初始化時讀取 navigator.onLine 取得當前連線狀態
 * - 監聽 window 的 online/offline 事件，即時更新狀態
 * - 在 SSR 環境下預設為 true（伺服器端無 navigator）
 *
 * @returns {NetworkStatus} 包含 isOnline 布林值的物件
 *
 * @example
 * ```tsx
 * const { isOnline } = useNetworkStatus();
 * if (!isOnline) {
 *   // 進入離線模式
 * }
 * ```
 */
export function useNetworkStatus(): NetworkStatus {
  /**
   * 初始狀態：
   * - 瀏覽器環境使用 navigator.onLine
   * - SSR 環境（無 window）預設為 true
   */
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return navigator.onLine;
    }
    return true;
  });

  /** 處理網絡恢復連線事件 */
  const handleOnline = useCallback(() => {
    setIsOnline(true);
  }, []);

  /** 處理網絡斷線事件 */
  const handleOffline = useCallback(() => {
    setIsOnline(false);
  }, []);

  useEffect(() => {
    /* 掛載時同步一次當前狀態，避免 SSR hydration 不一致 */
    setIsOnline(navigator.onLine);

    /* 註冊 online/offline 事件監聽 */
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    /* 清除事件監聽，防止記憶體洩漏 */
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return { isOnline };
}
